import React, { useEffect, useState } from 'react'

import { Controller, FormContext, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty, isNil, omitBy, union } from 'lodash'
import clsx from 'clsx'
import { Alert, AlertTitle } from '@material-ui/lab'
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Grow,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core'

import {
  Dai,
  DaiUploadDialog,
  FileUploadInput,
  JoditEditor,
  Label,
  SectionCard,
  TextFieldList,
  WhiteTooltip,
} from 'components'
import { ConfirmationDialog } from './components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useDaiPermissions from 'hooks/useDaiPermissions'

import constants from 'constants/index'
import * as service from 'service'
import theme from 'theme'
import schema from './schema'
import useStyles from './styles'

const AiInfo = () => (
  <WhiteTooltip
    title="Sugestão gerada pela análise automática de contrato. Verifique se está de acordo com sua necessidade."
    aiAlert
    cursor="help"
  >
    <InputAdornment position="start">
      <Dai.StarIcon size={14} />
    </InputAdornment>
  </WhiteTooltip>
)

const ContractForm = ({
  setLoading = false,
  handleRequestCompleted = () => {},
}) => {
  const [openDialog, setOpenDialog] = useState(false)
  const [openAlertDialog, setOpenAlertDialog] = useState(false)
  const [hasNotified, setHasNotified] = useState(false)
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [contractData, setContractData] = useState({})
  const [hasError, setHasError] = useState(false)

  const { TICKET_TYPE } = constants.tickets
  const { LOADING_TEXTS } = constants.technicalFaqs
  const { TAGS } = constants.dai.solutions
  const {
    DEFAULT_ERRO_MESSAGE,
    CONTRACT_FILE_TYPES,
    PRIVATE_VISIBILITY,
    CONTRACT_ANALYSIS,
  } = constants.technicalFaqs

  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()

  const { permitted: daiPermitted } = useDaiPermissions()

  const incomingContractFile = location.state?.contractFile
  const incomingContractingParties = location.state?.contractingParties
  const incomingContractedParties = location.state?.contractedParties

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      content: '',
      contractingParty: '',
      contractingParties: incomingContractingParties ?? [],
      contractedParty: '',
      contractedParties: incomingContractedParties ?? [],
      contractType: '',
      contractPurpose: '',
      hasClauses: '',
      attachments: incomingContractFile ?? null,
    },
  })

  const { control, errors, handleSubmit, watch, setValue, triggerValidation } =
    formMethods

  const contractingParties = watch('contractingParties')
  const contractedParties = watch('contractedParties')
  const hasClauses = watch('hasClauses')
  const attachments = watch('attachments')

  const permittedSubmit =
    !isEmpty(contractingParties) &&
    !isEmpty(contractedParties) &&
    !isEmpty(hasClauses) &&
    !isNil(attachments)

  const showSections = !isNil(attachments) || analysisLoading

  const canPreAnalysis = daiPermitted(TAGS.PRE_CONTRACT_ANALYSIS)

  const handleOpenDialog = () => setOpenDialog((prevState) => !prevState)
  const handleGoBack = () =>
    location.state?.returnTo
      ? history.push(location.state.returnTo)
      : history.goBack()

  const handleApplyAnalysis = () => {
    if (!isEmpty(contractData?.output?.contractingParties))
      setValue(
        'contractingParties',
        union(contractingParties, contractData.output.contractingParties),
      )
    if (!isEmpty(contractData?.output?.contractedParties))
      setValue(
        'contractedParties',
        union(contractedParties, contractData.output.contractedParties),
      )
    if (contractData?.output?.contractType)
      setValue('contractType', contractData?.output?.contractType)
    if (contractData?.output?.contractPurpose)
      setValue('contractPurpose', contractData?.output?.contractPurpose)
    if (contractData?.output?.hasClauses)
      setValue('hasClauses', contractData?.output?.hasClauses)
  }

  const handleFeedbackSubmit = async (daiFeedback) => {
    await service.dponet.juridicTickets.preContractAnalysisFeedback({
      daiResponseId: contractData?.daiResponseId,
      daiFeedback,
    })
  }

  const handleContractAnalysis = async () => {
    try {
      setHasError(false)

      const valid = await triggerValidation('attachments')
      if (!valid) {
        setHasError(true)
        return
      }

      setAnalysisLoading(true)
      setContractData({})

      const response = await service.dponet.juridicTickets.preContractAnalysis({
        ticket: { file: attachments },
      })

      setContractData(response?.data || {})
    } catch (error) {
      console.error(error)
      setHasError(error)
    } finally {
      setAnalysisLoading(false)
    }
  }

  const onSubmit = async (data = {}) => {
    try {
      setLoading(true)

      const {
        contractingParty,
        contractedParty,
        content,
        attachments,
        ...dataRest
      } = data

      const filteredDataRest = omitBy(dataRest, isEmpty)

      const payload = {
        ...filteredDataRest,
        attachments: helpers.file.toArray(attachments),
        ...(helpers.validates.jodit.hasContent(content) && {
          content: content,
        }),
        visibilityType: PRIVATE_VISIBILITY,
        title: CONTRACT_ANALYSIS,
        ticketType: TICKET_TYPE.JURIDIC_TICKET_TYPE,
      }

      await service.dponet.juridicTickets.create(payload)

      snackbar.open({
        message: 'Solicitação enviada com sucesso!',
        variant: 'success',
      })
      handleRequestCompleted()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || DEFAULT_ERRO_MESSAGE,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isNil(attachments) || !canPreAnalysis) return

    if (!hasNotified) {
      setOpenAlertDialog(true)
      setHasNotified(true)
    }

    handleContractAnalysis()
  }, [attachments])

  useEffect(() => {
    if (!analysisLoading && !isEmpty(contractData)) handleApplyAnalysis()
  }, [analysisLoading])

  return (
    <Box
      component="form"
      id="contract-solicitation-form"
      onSubmit={handleSubmit(onSubmit)}
      display="flex"
      flexDirection="column"
      gridGap={theme.spacing(2)}
    >
      <FormContext {...formMethods}>
        <SectionCard
          title="Envio do contrato"
          subTitle={
            canPreAnalysis
              ? 'Anexe o contrato para análise automática e facilite o preenchimento do formulário.'
              : 'Anexe o contrato para continuar com o preenchimento do formulário.'
          }
        >
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            {hasError && !isNil(attachments) && (
              <Alert severity="error">
                <AlertTitle>Falha na análise do contrato</AlertTitle>
                Não foi possível realizar a análise automática do contrato
                anexado. Verifique se o arquivo está de acordo com as regras
                aceitas e tente anexá-lo novamente. Caso o problema persista,
                entre em contato com o suporte.
              </Alert>
            )}
            <FileUploadInput
              controlName="attachments"
              accept={CONTRACT_FILE_TYPES}
              hasDownloadFile
              blockUnaccepted
              loading={analysisLoading}
            />
          </Box>
        </SectionCard>
        {showSections && !!contractData?.daiResponseId && (
          <Grow in>
            <Box>
              <Dai.FeedbackAlert
                daiResponseId={contractData?.daiResponseId}
                handleSubmit={handleFeedbackSubmit}
              />
            </Box>
          </Grow>
        )}
        {showSections && (
          <Collapse in>
            <Box>
              <SectionCard title="Informações sobre o contrato">
                <Dai.Loading texts={LOADING_TEXTS} loading={analysisLoading} />
                {!analysisLoading && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Label
                        title={
                          <Box display="flex" alignItems="center">
                            {!isEmpty(
                              contractData?.output?.contractingParties,
                            ) && <AiInfo />}
                            <span className={classes.span}>
                              Quem contrata *
                            </span>
                          </Box>
                        }
                        description="Informe o nome da empresa ou pessoa que está contratando o serviço ou firmando o acordo."
                        error={!!errors.contractingParties}
                        item
                        xs={12}
                      />
                      <TextFieldList
                        controlName="contractingParty"
                        arrayControlName="contractingParties"
                        containerTitle="Partes adicionadas"
                        titleContainerProps={{ color: 'text.secondary' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Label
                        title={
                          <Box display="flex" alignItems="center">
                            {!isEmpty(
                              contractData?.output?.contractedParties,
                            ) && <AiInfo />}
                            <span className={classes.span}>
                              Quem é contratado *
                            </span>
                          </Box>
                        }
                        description="Informe o nome da empresa ou pessoa que foi contratada para prestar o serviço ou cumprir o acordo."
                        error={!!errors.contractedParties}
                        item
                        xs={12}
                      />
                      <TextFieldList
                        controlName="contractedParty"
                        arrayControlName="contractedParties"
                        containerTitle="Partes adicionadas"
                        titleContainerProps={{ color: 'text.secondary' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Label
                        title="Tipo de contrato"
                        description="Indique o tipo ou modelo de contrato de acordo com o que está sendo firmado entre as partes. Exemplo: Prestação de serviços, Fornecimento, Parceria, Confidencialidade."
                        error={!!errors.contractType}
                        item
                        xs={12}
                      />
                      <Controller
                        name="contractType"
                        control={control}
                        as={
                          <TextField
                            color="primary"
                            variant="outlined"
                            error={!!errors.contractType}
                            helperText={errors?.contractType?.message}
                            fullWidth
                            {...(!!contractData?.output?.contractType && {
                              InputProps: {
                                startAdornment: <AiInfo />,
                              },
                            })}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Label
                        title="Para que serve este contrato (Finalidade)"
                        description="Descreva de forma simples o objetivo deste contrato, ou seja, o que ele pretende regular ou formalizar."
                        error={!!errors.contractPurpose}
                        item
                        xs={12}
                      />
                      <Controller
                        name="contractPurpose"
                        control={control}
                        as={
                          <TextField
                            color="primary"
                            variant="outlined"
                            error={!!errors.contractPurpose}
                            helperText={errors?.contractPurpose?.message}
                            fullWidth
                            {...(!!contractData?.output?.contractPurpose && {
                              InputProps: {
                                startAdornment: <AiInfo />,
                              },
                            })}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Label
                        title={
                          <Box display="flex" alignItems="center">
                            {!!contractData?.output?.hasClauses && <AiInfo />}
                            <span className={classes.span}>
                              O contrato possui cláusulas sobre LGPD? *
                            </span>
                          </Box>
                        }
                        description="Essa informação é importante para avaliarmos o cumprimento das normas de proteção de dados pessoais no contrato."
                        error={!!errors.hasClauses}
                        item
                        xs={12}
                      />
                      <Controller
                        control={control}
                        name="hasClauses"
                        as={
                          <FormControl error={!!errors.hasClauses}>
                            <RadioGroup
                              className={clsx({
                                [classes.radioError]: !!errors.hasClauses,
                              })}
                              value={hasClauses}
                              row
                            >
                              <FormControlLabel
                                value="Sim"
                                control={<Radio />}
                                label="Sim"
                              />
                              <FormControlLabel
                                value="Não"
                                control={<Radio />}
                                label="Não"
                              />
                              <FormControlLabel
                                value="Não sei"
                                control={<Radio />}
                                label="Não sei"
                              />
                            </RadioGroup>
                            {!!errors.hasClauses && (
                              <FormHelperText error={!!errors.hasClauses}>
                                {errors.hasClauses.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Label
                        title="Informações adicionais"
                        description="Caso tenha alguma observação adicional sobre o contrato, por favor, informe aqui."
                        item
                        xs={12}
                      />
                      <Controller
                        control={control}
                        as={
                          <JoditEditor.Editor
                            error={!!errors?.content}
                            helperText={errors?.content?.message}
                          />
                        }
                        name="content"
                        mode="onBlur"
                      />
                    </Grid>
                  </Grid>
                )}
              </SectionCard>
            </Box>
          </Collapse>
        )}
        <Box
          display="flex"
          gridGap={theme.spacing(1)}
          justifyContent="flex-end"
        >
          <Button variant="contained" onClick={handleGoBack}>
            Voltar
          </Button>
          <Tooltip title="Preencha todos os campos obrigatórios para prosseguir">
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
                disabled={!permittedSubmit}
              >
                Prosseguir
              </Button>
            </Box>
          </Tooltip>
        </Box>
        <ConfirmationDialog open={openDialog} setOpen={setOpenDialog} />
        <DaiUploadDialog open={openAlertDialog} setOpen={setOpenAlertDialog} />
      </FormContext>
    </Box>
  )
}

export default ContractForm
